import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  Toolbar,
  Typography,
  Icon,
  MenuList,
  Divider,
  ListItemIcon,
  Drawer,
  ListItemSecondaryAction,
  Badge,
  FormControlLabel,
  FormGroup,
  Switch,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LibraryIcon from '@mui/icons-material/Book';
import ReportsIcon from '@mui/icons-material/Assignment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { TasksIcon } from 'global/components/PerformIcons';
import NavRealm from '../NavRealm';
import logo from 'assets/logo.svg';
import ibacoslogo from 'assets/ibacos_innovation.svg';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {
  NAV_ACTIVE_CLASS,
  NavBarContentActions,
  NavBarContentActionsRow,
  NavBarContentChildren,
  NavBarContentRoot,
  NavBarContentRootWrap,
  NavBarGrow,
  NavBarMenuButton,
  NavBarRoot,
  NavBarTaskButton,
  NavBarTitle,
  NavCopyright,
  NavHeaderLogo,
  NavHeaderLogoWrapper,
  NavMenuItem,
  NavMenuTitle,
  NavNewLink,
  NavPerformLogo,
  NavTab,
  NavTabIcon,
  NavTabs,
} from './NavBar.styles';
import { useSelector } from 'react-redux';
import { get, size } from 'global/lib/lodash';
import { userHasPermissions } from 'redux/actions/userLogin';
import useNavSettings from 'global/hooks/useNavSettings';
import { stopProp } from 'global/utils';
import { useView } from 'global/hooks/useView';
import { AlertsDrop } from '../../user/alertsdrop';
import { useHashView } from 'feature/share/useHashView';

export default function NavBar(props) {
  const { pageValue } = props;
  const [drawerOpen, changeDrawerOpen] = useState(false);
  const { nav } = useNavSettings();
  const { active: hashViewActive } = useHashView();

  // PFM-2547: hide the top-level nav entirely when viewing via a share link —
  // the user is locked to a single piece of content and shouldn't see tabs / realm switcher.
  if (hashViewActive) return null;
  const pages = [
    {
      label: (
        <>
          <NavTabIcon as={TasksIcon} />
          tasks
        </>
      ),
      page: 'tasks',
      canDisplay: () => get(nav, 'tasks'),
    },
    {
      label: (
        <>
          <NavTabIcon as={ReportsIcon} />
          assessments
        </>
      ),
      page: 'reports',
      canDisplay: () => get(nav, 'assessments'),
    },
    {
      label: (
        <>
          <NavTabIcon as={LibraryIcon} />
          library
        </>
      ),
      page: 'library',
      canDisplay: () => get(nav, 'library'),
    },
    {
      label: (
        <>
          <NavTabIcon as={AutoAwesomeIcon} />
          agent
        </>
      ),
      page: 'agent',
      canDisplay: () => get(nav, 'agent'),
    },
  ];

  return (
    <>
      <Drawer open={drawerOpen} onClose={() => changeDrawerOpen(false)}>
        <NavHeaderLogoWrapper as={Link} to="/home" onClick={() => changeDrawerOpen(false)}>
          <SidebarLogo />
        </NavHeaderLogoWrapper>
        <Divider />
        <div tabIndex={0} role="button" onKeyDown={() => changeDrawerOpen(false)}>
          <SideList onClick={() => changeDrawerOpen(false)} />
        </div>
        <NavPerformLogo src={logo} alt="logo" />
        <NavCopyright>
          <NavNewLink href="https://docs.performwithibacos.com/faq" target="_blank" rel="noopener">
            <NavTabIcon as={Icon}>help</NavTabIcon>
            <span>Need Help?</span>
          </NavNewLink>
          <NavNewLink as={Link} to="/changelog">
            <NavTabIcon as={NewReleasesIcon} />
            <span>What's New</span>
          </NavNewLink>
          <p>&copy; {new Date().getFullYear()} IBACOS Perform</p>
        </NavCopyright>
      </Drawer>
      <NavBarRoot position="static" navHidden={!!props.hide}>
        <Toolbar>
          <NavBarMenuButton onClick={() => changeDrawerOpen(true)} sidebar={!!props.sidebar} color="inherit" aria-label="Menu" size="large">
            <MenuIcon />
          </NavBarMenuButton>
          <NavTabs value={pageValue}>
            {pages
              .filter((f) => f.canDisplay())
              .map((item, index) => (
                <NavTab data-testid={`navbar-tab-${item.page}`} component={Link} to={`/${item.page}`} label={item.label} value={item.page} key={index} />
              ))}
          </NavTabs>
          <NavBarTitle variant="h6" color="inherit">
            {props.pageTitle}
          </NavBarTitle>
          <NavBarGrow />
          <NavRealm />
          <AlertsDrop />
        </Toolbar>
      </NavBarRoot>
    </>
  );
}

export function NavBarContent(props) {
  const { actions, children } = props;
  // PFM-2547: hashed share-link viewers can't navigate away, so suppress the close button.
  const { active: hashViewActive } = useHashView();

  return (
    <NavBarContentRootWrap>
      <NavBarContentRoot position="static">
        <Toolbar>
          {!hashViewActive && (
            <NavBarMenuButton
              component={Link}
              to={typeof props.link === 'string' ? `/${props.link}` : props.link}
              color="inherit"
              aria-label="Menu"
              {...(props.closeProps || {})}
              size="large"
            >
              <CloseIcon />
            </NavBarMenuButton>
          )}
          <Typography
            variant="h6"
            sx={{
              color: 'inherit',
              minWidth: 0,
              // PFM-2628: when the action buttons are relocated to the row below
              // the title (assessment header on mobile), let the title fill the
              // full header width on small screens. Desktop sizing is unchanged.
              ...(props.actionsSlotId && { flexGrow: { xs: 1, md: 0 } }),
            }}
          >
            {props.pageTitle}
          </Typography>
          {/* PFM-2628: on mobile the assessment's actions move to the row below,
              so the grow spacer and the reserved action space would only steal
              width from the (now full-width) title — hide them below md when the
              actions have been relocated. Other headers are unaffected. */}
          <NavBarGrow sx={props.actionsSlotId ? { display: { xs: 'none', md: 'block' } } : undefined} />
          {actions && <NavBarContentActions>{actions}</NavBarContentActions>}
          {props.space && (
            <NavBarTaskButton
              sx={props.actionsSlotId ? { display: { xs: 'none', md: 'block' } } : undefined}
            />
          )}
        </Toolbar>
        {/* PFM-2628: portal target for the mobile-only Share/Download/Print
            cluster. Sits directly under the title and above the children
            (Location/Status row). Consumers (ReportTabs) render their action
            cluster into this node via a portal; it is empty/no-op otherwise and
            the cluster itself is hidden at `md`+ via its own styles. */}
        {props.actionsSlotId && <NavBarContentActionsRow id={props.actionsSlotId} />}
        {children && <NavBarContentChildren>{children}</NavBarContentChildren>}
      </NavBarContentRoot>
    </NavBarContentRootWrap>
  );
}

const TOPMENU = [
  { label: 'tasks', page: 'tasks', icon: <TasksIcon />, canDisplay: (p) => get(p, 'nav.tasks') },
  { label: 'assessments', page: 'reports', icon: <ReportsIcon />, canDisplay: (p) => get(p, 'nav.assessments') },
  { label: 'library', page: 'library', icon: <LibraryIcon />, canDisplay: (p) => get(p, 'nav.library') },
  { label: 'agent', page: 'agent', icon: <AutoAwesomeIcon />, canDisplay: (p) => get(p, 'nav.agent') },
];

const MENUITEMS = [
  { label: 'profile', page: 'profile', icon: <AccountCircleIcon /> },
  {
    label: 'alerts',
    page: 'alerts',
    icon: <NotificationsActiveIcon />,
    useSecondaryAction: (p) => {
      const view = useSelector((state) => get(state, 'view'));
      const amt = size(get(view, 'alerts.new'));
      if (amt > 0) {
        return <Badge badgeContent={amt >= 25 ? '25+' : amt} color={'primary'} />;
      }
    },
  },
  { label: 'manage users', page: 'users/manage', icon: <SupervisedUserCircleIcon />, disabled: () => !userHasPermissions(['manageUsers']) },
  { label: 'account settings', page: 'account/settings', icon: <SettingsIcon />, disabled: () => !userHasPermissions(['manageAccount']) },
];

function SideList(props) {
  const { nav } = useNavSettings();
  const { view, toggleTheme } = useView();

  const toggleDark = (e) => {
    stopProp(e);
    toggleTheme();
  };

  return (
    <div onClick={props.onClick} style={{ width: 250 }}>
      <MenuList>
        {TOPMENU.filter((tm) => tm.canDisplay({ nav })).map((text) => (
          <NavMenuItem component={NavLink} activeClassName={NAV_ACTIVE_CLASS} to={`/${text.page}`} key={text.label} data-testid={text.label}>
            <ListItemIcon>{text.icon}</ListItemIcon>
            <NavMenuTitle primary={text.label} />
          </NavMenuItem>
        ))}
      </MenuList>
      <Divider />
      <MenuList>
        {MENUITEMS.map((text) => (
          <NavMenuItem component={NavLink} activeClassName={NAV_ACTIVE_CLASS} to={`/${text.page}`} key={text.page} disabled={get(text, 'disabled', () => false)()}>
            <ListItemIcon>{text.icon}</ListItemIcon>
            <NavMenuTitle primary={text.label} />
            {text.useSecondaryAction && <ListItemSecondaryAction>{text.useSecondaryAction(props)}</ListItemSecondaryAction>}
          </NavMenuItem>
        ))}
      </MenuList>
      <Divider />
      <MenuList>
        <NavMenuItem component={NavLink} activeClassName={NAV_ACTIVE_CLASS} to="/logout">
          <ListItemIcon>
            <ExitToAppIcon />
          </ListItemIcon>
          <NavMenuTitle primary="Logout" />
        </NavMenuItem>
        <NavMenuItem onClick={toggleDark}>
          <FormGroup>
            <FormControlLabel control={<Switch checked={get(view, 'style.palette.mode', 'light') === 'dark'} />} label="Dark Theme" />
          </FormGroup>
        </NavMenuItem>
      </MenuList>
    </div>
  );
}

function SidebarLogo(props) {
  // PFM-2625: builder logo lives in Redux at view.style.props.logo (set in
  // redux/actions/view.js). Fall back to the IBACOS logo only when no builder
  // logo is set. Mirrors the selector used in feature/report/Tabs/index.js.
  const builderLogo = useSelector((state) => get(state, 'view.style.props.logo'));

  // PFM-2625 follow-up: render the styled <img> directly instead of through EImage
  // (as={Image} unsetWidth). EImage keeps its container at opacity:0 until an
  // async calc() (setTimeout 500ms on load/resize) measures real dimensions. The
  // drawer is a temporary MUI Drawer, so on FIRST open the logo's calc() runs
  // against a not-yet-laid-out element and dims never resolve -> the logo loads
  // but stays invisible until the SECOND open. A plain <img> shows immediately on
  // first open. object-fit:contain preserves aspect ratio (no stretch); the
  // surrounding NavHeaderLogoWrapper centers it via flex.
  return <NavHeaderLogo src={builderLogo || ibacoslogo} alt="" sx={{ objectFit: 'contain' }} />;
}
